import { Typography } from "@mui/material";

import {
  faqs,
  howItWorks,
  createPerkTotalLabel,
  purchaseSeatsLanguages,
  redeemGiftCardLanguages,
  purchaseGiftCardLanguages,
  AnalyticsHeader,
  inventoryCardSuiteModal,
  InventoryCard,
  leftFieldPorchSuiteNames,
  LeftFieldPorchContent,
  StudioSuiteContent,
  studioSuiteNames,
  SuitesContent,
  LegendsContent,
  legendSuiteNames,
} from "./helpers";
import { routes } from "@/global/routes";
import {
  DbacksTCLanguage,
  DbacksHamburgerTCListItems,
  DbacksAdditionalDetailsDisclaimerTC,
} from "../../tc_helper_dbacks";
import { createJsxLink } from "@/global/utils/utils.dom";
import { inventoryTypeUIDs, vendorUUIDs } from "../../constants";

import type { TeamConfig } from "@/global/hooks/useTeamConfig";

import suiteImage3 from "public/images/diamondbacks/image.png";
import foodImage1 from "public/images/diamondbacks/dbacks_food1.jpg";
import BackgroundImage from "public/images/diamondbacks/dbacks_bg.jpeg";
import SeatMap3dPreview from "public/images/diamondbacks/seat_map_preview.png";

const LogoImage = "https://ss-leagues.s3.amazonaws.com/MLB-3/AZD-UPDATED.png";
const BannerImage =
  "https://ss-leagues.s3.amazonaws.com/AZDBacks%20Assets/MK05-10251389_Web_-_2026_Attend_Site_Specs-1486x148-GROUP_SPACES.jpg";

const tableSuiteImages = [
  "https://ss-leagues.s3.amazonaws.com/AZDBacks%20Assets/AZD%20Suites%20Images/Banner%20Patio.jpg",
];
const strikeZoneImage = [
  "https://ss-leagues.s3.amazonaws.com/AZDBacks%20Assets/AZD%20Suites%20Images/Strike%20Zone.jpg",
];

const config: TeamConfig = {
  teamSchema: "dbacks_groupspaces",
  browserTitle: "Arizona Diamondbacks",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  mlbTeamId: 109,
  teamName: "Arizona Diamondbacks",

  // seat map configs
  seatMapConfigs: {
    venueId: "nam-us-10167-diamondbacks-sec",
    mapId: "blockmap2d",

    suiteSectionNameMapping: {
      "Studio@122A": ["Studio A"],
      "Studio@122B": ["Studio B"],
      "Legends Suite C": ["Legends C"],
      "Legends Suite D": ["Legends D"],
      "Legends Suite E": ["Legends E"],
      "Legends Suite F": ["Legends F"],
    },

    isShowAvailableSeatsButtonHidden: true,
    dvmFallback3dViewSectionId: "S_144",

    suiteImages: [],

    specificSectionImages: {
      TABLEB: tableSuiteImages,
      TABLEC: tableSuiteImages,
      TABLED: tableSuiteImages,
      TABLEE: tableSuiteImages,
      STKZON: strikeZoneImage,
    },
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: false,
  inventoryType: inventoryTypeUIDs.suiteLevelV2,

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 1,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: true,
    allowManualCartInSimilar: false,
    isCheckoutEditSeatsEnabled: false,
    isDiffSeatAlertEnabled: false,
    invPreviewAccountGroup: ["0"],
    isPerksEnabled: true,
    isCheckoutAdditionalDetailsEnabled: true,
    hideViewTicketsInThankYouPage: true,

    allowCheckoutFromPreview: true,
  },

  oktaClientId: "",
  oktaIssuer: "",
  platform: vendorUUIDs.ticketmaster,
  refreshInventoryWhenEventClick: false,

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
    AnalyticsHeader: AnalyticsHeader,
  },

  isSaveCardAllowed: true,

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  // urls
  accountManagerUrl: "https://am.ticketmaster.com/azdbacks/my-events",

  // images
  images: {
    logoNameImage: "",
    faviconImage: LogoImage,
    findGamesBannerImage: BannerImage,
    authBackgroundImage: BackgroundImage.src,
    giftCardStandalonePageImage: BannerImage,
    seatMap3dPreviewImage: SeatMap3dPreview.src,
    logoImage: LogoImage + "?width=84&height=66",
    checkoutStatusPageImage: BackgroundImage.src,
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: true,
    },
    bottomBar: {
      showGameSelectionProgress: true,
    },

    inventoryContainer: {
      emptyStateTitle: "No D-backs Suites Available.",
      emptyStateDesc: (
        <>
          Try another game or email{" "}
          {createJsxLink("suites@dbacks.com", "mailto:suites@dbacks.com")} for
          suite availability to this game
        </>
      ),
      getGridCellHeight: (arg) => (arg?.mdDown ? 260 : 190),
    },

    inventoryCardSettings: {
      isPBLineItemsVisible: false,
      isParkingPriceVisible: true,
      seatCountLabel: "Tickets Included",

      suiteLevelInventoryCard: {
        customButtons: [
          {
            id: 1,
            label: "More Info",
            onClick: () => {
              inventoryCardSuiteModal.getState().set({
                isOpen: true,
                title: "More Info",
                content: <LeftFieldPorchContent />,
              });
            },
            suiteNames: leftFieldPorchSuiteNames,
          },
          {
            id: 2,
            label: "More Info",
            onClick: () => {
              inventoryCardSuiteModal.getState().set({
                isOpen: true,
                title: "More Info",
                content: <StudioSuiteContent />,
              });
            },
            suiteNames: studioSuiteNames,
          },
          {
            id: 3,
            label: "More Info",
            onClick: () => {
              inventoryCardSuiteModal.getState().set({
                isOpen: true,
                title: "More Info",
                content: <SuitesContent />,
              });
            },
            suiteNames: Array.from({ length: 57 }, (_, i) => `Suite ${i + 1}`), // Suites 1 to 57
          },
          {
            id: 4,
            label: "More Info",
            onClick: () => {
              inventoryCardSuiteModal.getState().set({
                isOpen: true,
                title: "More Info",
                content: <LegendsContent />,
              });
            },
            suiteNames: legendSuiteNames,
          },
        ],
        renderBody: [
          {
            id: 1,
            component: () => <InventoryCard />,
          },
        ],
      },
    },

    perks: {
      hideAddAllButton: true,
      images: {
        foodUpgrade: [foodImage1.src],
        handsHistory: [suiteImage3.src],
      },
      displayOnlyPrice: ["handsHistory", "foodUpgrade"],
      isOpenByDefault: true,
    },
    checkoutPerkSubtotals: {
      perkTotalLabel: createPerkTotalLabel,
    },

    eventCardSettings: {
      noInventoryMessage: "Email groups@dbacks.com for availability.",
      hideSoldOutTag: true,
    },

    checkoutPage: {
      checkoutForm: {
        hideContractTitle: true,
        hideContractLastName: true,
        hideContractFirstName: true,
        hideContractAddress: false,
        sameAsContractSignerDefaultChecked: true,
        optionalAdditionalDetailsContractTitle: true,
        optionalAdditionalDetailsContractLastName: true,
        optionalAdditionalDetailsContractFirstName: true,
      },
    },

    findGames: {
      setEventsWithoutSorting: true,
    },
  },

  colors: {
    primary: "#AA182C",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: <DbacksHamburgerTCListItems />,
    checkoutTermsAndCondition: <DbacksTCLanguage />,
    registrationTermsAndCondition: <DbacksTCLanguage />,
    additionalDetailsDisclaimerTC: <DbacksAdditionalDetailsDisclaimerTC />,
    purchaseSeats: purchaseSeatsLanguages,
    purchaseGiftCard: purchaseGiftCardLanguages,
    redeemGiftCard: redeemGiftCardLanguages,

    checkoutServiceNote: (
      <Typography variant="body2" color="textSecondary">
        Your reservation is not yet complete until notification from the Arizona
        Diamondbacks confirming your reservation has been accepted in accordance
        with our agreement.
      </Typography>
    ),

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
